import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormGroup, FormArray, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject, forkJoin } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { 
  LocationStaffService, 
  LocationStaffAdminDto, 
  StaffStatusDto 
} from '../../../services/location-staff.service';
import { MasterService } from '../../../../../master/services/master.service';
import { MasterModel } from '../../../../../master/models/master.model';
import { LocationStaffDeleteDialogComponent } from './location-staff-delete-dialog.component';
import { LocationStaffDialogComponent } from './location-staff-dialog.component';

@Component({
    selector: 'app-location-staff-section',
    templateUrl: './location-staff-section.component.html',
    styleUrls: ['./location-staff-section.component.css'],
    standalone: false
})
export class LocationStaffSectionComponent implements OnInit, OnDestroy {
  @Input() formGroup!: UntypedFormGroup;
  @Input() locationId: string | null = null;

  staffStatuses: StaffStatusDto[] = [];
  staff: LocationStaffAdminDto[] = [];

  searchTerm = '';
  filterStatus: number | null = null;

  isLoading = false;
  isLoadingStatuses = false;

  errorMessage: string | null = null;

  totalItems = 0;
  totalPages = 1;
  currentPage = 1;
  pageSize = 20;

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private dialog: MatDialog,
    private staffService: LocationStaffService,
    private masterService: MasterService
  ) {}

  ngOnInit(): void {
    console.log('LocationStaffSectionComponent initialized with locationId:', this.locationId);
    this.setupSearchDebounce();
    this.loadStaffStatuses();
    this.loadStaff();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearchDebounce(): void {
    this.searchSubject
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(searchTerm => {
        this.searchTerm = searchTerm;
        this.currentPage = 1;
        this.loadStaff();
      });
  }

  onSearchChange(value: string): void {
    this.searchSubject.next(value);
  }

  private loadStaffStatuses(): void {
    this.isLoadingStatuses = true;
    this.staffService.getStaffStatuses()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.isSuccess && response.values) {
            this.staffStatuses = response.values;
          }
          this.isLoadingStatuses = false;
        },
        error: (err) => {
          console.error('Error loading staff statuses:', err);
          this.isLoadingStatuses = false;
        }
      });
  }

  loadStaff(): void {
    if (!this.locationId) {
      console.warn('No locationId available');
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;

    this.staffService.searchLocationStaff(
      this.locationId,
      this.searchTerm || undefined,
      this.filterStatus !== null ? this.filterStatus : undefined,
      this.currentPage,
      this.pageSize
    )
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (response) => {
        this.isLoading = false;
        
        if (response.isSuccess && response.value) {
          this.staff = response.value.staff || [];
          this.totalItems = response.value.totalCount || 0;
          this.totalPages = response.value.totalPages || 1;
          this.currentPage = response.value.page || 1;
        } else if (response.error) {
          this.errorMessage = this.getErrorMessage(response.error);
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error loading staff:', err);
        this.errorMessage = 'Error al cargar el personal. Por favor, inténtalo de nuevo.';
      }
    });
  }

  onStatusFilterChange(statusId: number | null): void {
    this.filterStatus = statusId;
    this.currentPage = 1;
    this.loadStaff();
  }

  getStatusName(statusId: number): string {
    const status = this.staffStatuses.find(s => s.statusMerchantStaffId === statusId);
    return status ? status.name : 'Desconocido';
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadStaff();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadStaff();
    }
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.loadStaff();
    }
  }

  openStaffDialog(staff?: LocationStaffAdminDto): void {
    if (!this.locationId) {
      console.error('No se puede abrir el diálogo: locationId no disponible');
      this.errorMessage = 'No se puede abrir el diálogo. ID del centro de trabajo no disponible.';
      return;
    }

    const dialogRef = this.dialog.open(LocationStaffDialogComponent, {
      width: '700px',
      maxWidth: '95vw',
      disableClose: false,
      data: {
        staff: staff || null,
        locationId: this.locationId,
        staffStatuses: this.staffStatuses
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadStaff();
      }
    });
  }

  confirmDelete(staff: LocationStaffAdminDto): void {
    if (!this.locationId) {
      console.error('No se puede eliminar: locationId no disponible');
      this.errorMessage = 'No se puede eliminar. ID del centro de trabajo no disponible.';
      return;
    }

    const dialogRef = this.dialog.open(LocationStaffDeleteDialogComponent, {
      width: '500px',
      maxWidth: '96vw',
      data: {
        staffName: staff.displayName
      }
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.isLoading = true;
        this.errorMessage = null;

        this.staffService.deleteLocationStaff(
          this.locationId!,
          staff.merchantStaffId
        ).pipe(
          takeUntil(this.destroy$)
        ).subscribe(
          response => {
            this.isLoading = false;
            if (response && response.isSuccess) {
              this.loadStaff();
            } else {
              this.errorMessage = response.error ? this.getErrorMessage(response.error) : 'Error desconocido al eliminar';
            }
          },
          error => {
            this.isLoading = false;
            console.error('Error deleting staff:', error);
            this.errorMessage = 'Error al eliminar el personal. Por favor, intente nuevamente.';
          }
        );
      }
    });
  }

  private getErrorMessage(error: any): string {
    if (error && error.messages && Array.isArray(error.messages)) {
      return error.messages.join(', ');
    }
    return 'Ha ocurrido un error desconocido';
  }

  clearError(): void {
    this.errorMessage = null;
  }
  public trackByIndex(i: number) { return i; }

}
